import { useMemo } from 'react'

import {
  getIsSpotifyUrl,
  getSpotifyTrackId,
} from '../spotifyTracks-process/utils'

import {
  SpotifyTrack,
  useLazyGetSpotifyTrackByIdQuery,
  useLazyGetSpotifyTracksBySearchQuery,
} from '.'

type UseSearchSpotifyTrack = {
  searchTracks: (searchInput: string) => Promise<void>
  tracks: SpotifyTrack[]
  loading: boolean
  isSuccess?: boolean
  error: string | undefined
}

export const useSearchSpotifyTrack = (): UseSearchSpotifyTrack => {
  const [
    getTracks,
    {
      data: tracksData,
      isLoading: tracksLoading,
      isError: isTracksError,
      isSuccess: tracksSuccess,
    },
  ] = useLazyGetSpotifyTracksBySearchQuery()

  const [
    getTrack,
    {
      data: trackData,
      isLoading: trackLoading,
      isError: isTrackError,
      isSuccess: trackSuccess,
    },
  ] = useLazyGetSpotifyTrackByIdQuery()

  const searchTracks = async (searchInput: string): Promise<void> => {
    if (getIsSpotifyUrl(searchInput)) {
      const trackID = getSpotifyTrackId(searchInput)
      await getTrack(trackID)
    } else {
      await getTracks(searchInput)
    }
  }

  const responseData = tracksData || (trackData ? [trackData] : [])
  const isLoading = tracksLoading || trackLoading
  const isNoTracksError = !responseData || responseData.length === 0
  const error = useMemo(() => {
    if (isTrackError) {
      return 'Search by track name is temporarily unavailable. As an alternative, please add the track URL.'
    }
    if (isTracksError) {
      return 'Searching by URL is temporarily unavailable. Please try searching for the track by name instead.'
    }
    if (isNoTracksError && (tracksSuccess || trackSuccess)) {
      return 'No tracks were found.'
    }
    return ''
  }, [
    isTrackError,
    isTracksError,
    isNoTracksError,
    tracksSuccess,
    trackSuccess,
  ])

  return {
    searchTracks,
    error,
    tracks: responseData,
    isSuccess: tracksSuccess || trackSuccess,
    loading: isLoading,
  }
}
